'use client'
import { useEffect, useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import api from '@/shared/api'
import { ENDPOINTS, ROLES } from '@/shared/constants'
import { useAppSelector } from '@/store/hooks'
import { CostHistory, MonthlyCostEstimate } from '@/models/whatsappCosts'
import {
  costError,
  currentCostMonth,
  emptyCostVolumes,
  simulateMonthlyCost,
} from '@/shared/whatsappCosts'
import OfficialApiHelp from './OfficialApiHelp'

const formatCost = (value: string, currency: string) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(
    Number(value)
  )

export default function MonthlyCostCalculator({
  compact = false,
  directOfficialEnabled,
}: {
  compact?: boolean
  directOfficialEnabled?: boolean
}) {
  const { user } = useAppSelector((state) => state.auth)
  const allowed = user?.role?.id === ROLES.COMPANY_ADMIN
  const [history, setHistory] = useState<CostHistory>()
  const month = currentCostMonth()
  const [currency, setCurrency] = useState<'USD' | 'BRL'>('BRL')
  const [volumes, setVolumes] = useState({
    openings: 0,
    replies: 0,
    broadcasts: 0,
  })
  const [estimate, setEstimate] = useState<MonthlyCostEstimate>()
  const [historyLoading, setHistoryLoading] = useState(true)
  const [loading, setLoading] = useState(false)
  const [historyError, setHistoryError] = useState('')
  const [error, setError] = useState('')
  const [retry, setRetry] = useState(0)
  const [historyRetry, setHistoryRetry] = useState(0)
  const [edited, setEdited] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const fromHistory = (data: CostHistory) => ({
    openings: data.direct.utility,
    replies:
      data.direct.service +
      data.direct.marketing +
      data.direct.authentication +
      data.direct.unknown,
    broadcasts:
      data.broadcast.marketing +
      data.broadcast.utility +
      data.broadcast.authentication +
      data.broadcast.service +
      data.broadcast.unknown,
  })
  useEffect(() => {
    if (!allowed) return
    const controller = new AbortController()
    setHistoryLoading(true)
    setHistoryError('')
    api
      .get<CostHistory>(ENDPOINTS.WHATSAPP_COSTS.HISTORY, {
        signal: controller.signal,
      })
      .then(({ data }) => {
        setHistory(data)
        setVolumes(fromHistory(data))
        setEdited(false)
      })
      .catch((e) => {
        if (!controller.signal.aborted) setHistoryError(costError(e))
      })
      .finally(() => {
        if (!controller.signal.aborted) setHistoryLoading(false)
      })
    return () => controller.abort()
  }, [allowed, historyRetry])
  const ready = !!history?.months.length || edited
  useEffect(() => {
    setEstimate(undefined)
    setError('')
    if (!allowed || historyLoading || !ready) {
      setLoading(false)
      return
    }
    const controller = new AbortController()
    setLoading(true)
    const timer = window.setTimeout(() => {
      simulateMonthlyCost(
        month,
        {
          ...emptyCostVolumes(),
          utility: volumes.openings,
          service: volumes.replies,
        },
        { ...emptyCostVolumes(), marketing: volumes.broadcasts },
        currency,
        controller.signal
      )
        .then(setEstimate)
        .catch((e) => {
          if (!controller.signal.aborted) setError(costError(e))
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false)
        })
    }, 300)
    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [allowed, historyLoading, ready, month, currency, volumes, retry])
  if (!allowed) return null
  const total = estimate?.totals[0]
  const subtotal = (group: 'direct' | 'broadcast') => {
    if (
      !estimate ||
      estimate.periods.some((p) => p[group].status !== 'complete')
    )
      return 'Não estimado'
    return formatCost(
      estimate.periods
        .reduce((sum, p) => sum + Number(p[group].knownSubtotal), 0)
        .toFixed(2),
      currency
    )
  }
  const monthLabel = new Date(`${month}-02T12:00:00Z`).toLocaleDateString(
    'pt-BR',
    { month: 'long', year: 'numeric', timeZone: 'UTC' }
  )
  const fields = [
    {
      key: 'openings',
      label: 'Atendimentos iniciados por você',
      hint: 'Uma mensagem inicial de utilidade por abertura ativa.',
    },
    {
      key: 'replies',
      label: 'Demais mensagens no atendimento',
      hint: 'Estimadas como mensagens de serviço.',
    },
    {
      key: 'broadcasts',
      label: 'Mensagens em campanhas e listas',
      hint: 'Estimadas como marketing. 1 envio para 100 contatos = 100 mensagens.',
    },
  ] as const
  return (
    <Paper variant="outlined" sx={{ p: compact ? 2 : 3, minWidth: 0 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="bodyEmphasis">
            Simulação de custo mensal da API oficial
          </Typography>
          <Typography variant="bodySm" color="text.secondary">
            {month === '2026-10'
              ? `Projeção após o início da cobrança de Serviço em 01/10/2026 · ${currency}`
              : `${monthLabel} · ${currency}`}
          </Typography>
        </Box>
        <Alert severity="info">
          Este valor é uma estimativa, não uma cobrança realizada. A cobrança
          efetiva da Meta depende das mensagens entregues pela API oficial.
          {directOfficialEnabled === false &&
            ' O atendimento 1:1 está desativado na API oficial; o valor dessa parte mostra apenas quanto custaria se fosse ativado.'}
        </Alert>
        {historyLoading || loading ? (
          <Stack direction="row" spacing={1} alignItems="center" role="status">
            <CircularProgress size={22} />
            <Typography>Calculando sua estimativa…</Typography>
          </Stack>
        ) : total ? (
          <>
            <Box aria-live="polite">
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: 30, sm: 36 },
                  overflowWrap: 'anywhere',
                }}
              >
                {total.total !== null
                  ? formatCost(total.total, currency)
                  : 'Estimativa incompleta'}
              </Typography>
              {total.total === null && Number(total.knownSubtotal) > 0 && (
                <Typography variant="bodySm">
                  Parte estimada: {formatCost(total.knownSubtotal, currency)}
                </Typography>
              )}
              <Typography variant="bodySm" color="text.secondary">
                {edited
                  ? 'Com os volumes que você informou.'
                  : `Com base na média dos últimos ${history?.months.length} meses completos.`}
              </Typography>
            </Box>
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between" gap={1}>
                <Typography>
                  Atendimento 1:1
                  {directOfficialEnabled === false && ' (API oficial desativada)'}
                </Typography>
                <Typography fontWeight={600}>{subtotal('direct')}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between" gap={1}>
                <Typography>Campanhas e listas</Typography>
                <Typography fontWeight={600}>
                  {subtotal('broadcast')}
                </Typography>
              </Stack>
            </Stack>
            {total.total === null && (
              <Alert severity="info">
                Faltam tarifas para concluir o cálculo. Solicite o cadastro ao
                suporte.
              </Alert>
            )}
          </>
        ) : error ? (
          <Alert
            severity="warning"
            action={
              <Button onClick={() => setRetry((n) => n + 1)}>
                Tentar novamente
              </Button>
            }
          >
            Não foi possível calcular agora.
          </Alert>
        ) : (
          <Box>
            <Typography color="text.secondary">
              Ainda não temos histórico suficiente para estimar.
            </Typography>
            <Button onClick={() => setExpanded(true)}>
              Informar meu volume mensal
            </Button>
          </Box>
        )}
        {!!history?.excluded && (
          <Typography variant="bodySm" color="text.secondary">
            Em média, {history.excluded.toLocaleString('pt-BR')} mensagens
            enviadas por mês não entraram no cálculo porque o contato não tinha
            um número brasileiro válido cadastrado.
          </Typography>
        )}
        <Accordion
          expanded={expanded}
          onChange={(_, value) => setExpanded(value)}
          disableGutters
          elevation={0}
          sx={{ '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
            <Typography color="primary">Ajustar estimativa</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0 }}>
            <Stack spacing={2}>
              {fields.map(({ key, label, hint }) => (
                <TextField
                  key={key}
                  label={label}
                  type="number"
                  value={volumes[key]}
                  helperText={hint}
                  onChange={(e) => {
                    setEdited(true)
                    setVolumes((old) => ({
                      ...old,
                      [key]: Math.max(
                        0,
                        Math.min(
                          100000000,
                          Math.trunc(Number(e.target.value) || 0)
                        )
                      ),
                    }))
                  }}
                  slotProps={{ htmlInput: { min: 0, max: 100000000, step: 1 } }}
                />
              ))}
              {!compact && (
                <TextField
                  fullWidth
                  select
                  label="Moeda da conta WhatsApp"
                  value={currency}
                  onChange={(e) =>
                    setCurrency(e.target.value as 'USD' | 'BRL')
                  }
                >
                  <MenuItem value="BRL">Real (BRL)</MenuItem>
                  <MenuItem value="USD">Dólar (USD)</MenuItem>
                </TextField>
              )}
              {!!history?.months.length && (
                <Button
                  onClick={() => {
                    setVolumes(fromHistory(history))
                    setEdited(false)
                  }}
                >
                  Restaurar média do histórico
                </Button>
              )}
              {!!historyError && (
                <Button onClick={() => setHistoryRetry((n) => n + 1)}>
                  Tentar carregar histórico novamente
                </Button>
              )}
              {!compact && (
                <Typography variant="bodySm" color="text.secondary">
                  Antes de outubro, esta projeção já inclui a cobrança de
                  Serviço que começa em 01/10/2026. Depois disso, usa
                  automaticamente o mês atual. Escolha a moeda cobrada pela
                  Meta; não fazemos conversão.
                </Typography>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>
        <Divider />
        <Typography variant="bodySm" color="text.secondary">
          {compact
            ? 'Custo Meta estimado, sem descontos ou gratuidades. O valor final pode variar.'
            : 'Somente custo Meta para números brasileiros. Tarifas base, sem descontos por volume ou gratuidades de 72 horas; o valor final pode variar.'}
        </Typography>
        {!compact && <OfficialApiHelp compact />}
      </Stack>
    </Paper>
  )
}
